import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import PropTypes from "prop-types";
import TraceIdLink from "./TraceIdLink";

const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return String(value);
  return `$${num.toFixed(2)}`;
};

const safeText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
};

class TileErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

TileErrorBoundary.propTypes = {
  children: PropTypes.node,
  fallback: PropTypes.node,
};

const OperationResponsePanel = ({ result, onTraceIdClick }) => {
  const { ok, status, message, products, raw, traceId } = result;
  const hasProducts = Array.isArray(products) && products.length > 0;
  const hasMessage = Boolean(message && message.trim());

  return (
    <Box
      sx={{
        mt: 2,
        borderRadius: "16px",
        border: `1px solid ${ok ? "rgba(22, 163, 74, 0.30)" : "rgba(220, 38, 38, 0.30)"}`,
        backgroundColor: ok ? "rgba(240, 253, 244, 0.8)" : "rgba(254, 242, 242, 0.8)",
        p: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.85rem",
            color: ok ? "#15803d" : "#b91c1c",
          }}
        >
          {ok ? "Success" : "Request failed"}
          {status ? ` · ${status}` : ""}
        </Typography>
        {traceId && onTraceIdClick && (
          <TraceIdLink traceId={traceId} onClick={onTraceIdClick} />
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: "14px",
            backgroundColor: "#fff",
            border: "1px solid rgba(15, 23, 42, 0.06)",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Box
            component="pre"
            sx={{
              m: 0,
              maxHeight: 460,
              overflow: "auto",
              fontFamily: "'Roboto Mono', ui-monospace, monospace",
              fontSize: "0.78rem",
              color: "#334155",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {raw}
          </Box>
        </Paper>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}>
          {hasProducts && (
            <TileErrorBoundary
              fallback={
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.75,
                    borderRadius: "14px",
                    backgroundColor: "#fff",
                    border: "1px solid rgba(220, 38, 38, 0.20)",
                  }}
                >
                  <Typography sx={{ fontSize: "0.8rem", color: "#b91c1c", fontWeight: 700 }}>
                    Couldn&apos;t render product tiles for this response.
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", mt: 0.5 }}>
                    See the raw response on the left for full details.
                  </Typography>
                </Paper>
              }
            >
              <Box
                sx={{
                  display: "flex",
                  overflowX: "auto",
                  gap: 1.5,
                  pb: 1,
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                {products.map((product, idx) => {
                  const link = typeof product.link === "string" ? product.link : "";
                  const linkProps = link
                    ? { component: "a", href: link, target: "_blank", rel: "noopener noreferrer" }
                    : {};
                  const title = safeText(product.title);
                  const productType = safeText(product.productType);
                  const imageUrl = typeof product.imageUrl === "string" ? product.imageUrl : "";
                  const price = formatPrice(product.price);
                  const originalPrice = formatPrice(product.originalPrice);
                  const onSale = Boolean(product.sale) && originalPrice && originalPrice !== price;
                  const variantLine = [safeText(product.displayColor), safeText(product.displaySize)]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <Paper
                      key={safeText(product.id) || idx}
                      elevation={0}
                      {...linkProps}
                      sx={{
                        minWidth: 168,
                        maxWidth: 168,
                        borderRadius: "14px",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#fff",
                        border: "1px solid rgba(255, 132, 76, 0.16)",
                        textDecoration: "none",
                        color: "inherit",
                        cursor: link ? "pointer" : "default",
                        transition: "box-shadow 0.15s ease, transform 0.15s ease",
                        "&:hover": link
                          ? { boxShadow: "0 12px 26px rgba(236, 123, 55, 0.16)", transform: "translateY(-2px)" }
                          : {},
                      }}
                    >
                      <Box sx={{ height: 168, backgroundColor: "#f8fafc", overflow: "hidden", position: "relative" }}>
                        {imageUrl && (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt={title || "Product"}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        )}
                        {productType && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              px: 1,
                              py: 0.25,
                              borderRadius: "999px",
                              backgroundColor: "rgba(255,255,255,0.92)",
                              fontSize: "0.6rem",
                              fontWeight: 700,
                              color: "#f97316",
                              textTransform: "uppercase",
                              letterSpacing: "0.02em",
                            }}
                          >
                            {productType}
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ p: 1.25, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            lineHeight: 1.25,
                            fontSize: "0.75rem",
                            color: "#202531",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            minHeight: "1.9em",
                            mb: 0.5,
                          }}
                        >
                          {title}
                        </Typography>
                        {variantLine && (
                          <Typography sx={{ fontSize: "0.65rem", color: "#6b7280", mb: 0.75 }}>
                            {variantLine}
                          </Typography>
                        )}
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mt: "auto" }}>
                          {price && (
                            <Typography
                              sx={{ fontWeight: 800, fontSize: "0.85rem", color: onSale ? "#dc2626" : "#202531" }}
                            >
                              {price}
                            </Typography>
                          )}
                          {onSale && (
                            <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", textDecoration: "line-through" }}>
                              {originalPrice}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </TileErrorBoundary>
          )}

          {hasMessage && (
            <Paper
              elevation={0}
              sx={{
                p: 1.75,
                borderRadius: "14px",
                backgroundColor: "#fff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
              }}
            >
              <Typography sx={{ fontSize: "0.85rem", color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                {message}
              </Typography>
            </Paper>
          )}

          {!hasProducts && !hasMessage && (
            <Typography sx={{ fontSize: "0.8rem", color: "#9ca3af", fontStyle: "italic" }}>
              No products or message to display.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

OperationResponsePanel.propTypes = {
  result: PropTypes.shape({
    ok: PropTypes.bool,
    status: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    message: PropTypes.string,
    products: PropTypes.array,
    raw: PropTypes.string,
    traceId: PropTypes.string,
  }).isRequired,
  onTraceIdClick: PropTypes.func,
};

export default OperationResponsePanel;
